import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import TableContainer from '@hud/web-components/Container/TableContainer';
import { DefaultTable, LinkCell } from '@hud/web-components/Table/Table';
import { Search } from '@hud/web-components/Search/Search';

import Pagination from '@hud/web-components/Pagination/Pagination';
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { HorizontalContainer } from '@hud/web-components/Container/HorizontalContainer';
import Skeleton from '@hud/web-components/Skeleton/Skeleton';
import { useGlobalQueryString } from '../../../hooks/useGlobalFilter';
import { ServicesAutocompleteResponse } from '@hud/portal-dtos/webapp-autocomplete.dto';
import { useInputState } from '@mantine/hooks';
import { MultiSelectFilter } from '@hud/web-components/Filters/Filters';
import Menu from '@hud/web-components/Menu/Menu';

import {
  ClearAllButton,
  FiltersIcon,
  FiltersMenuButton,
} from '../../../components/FunctionsTable/FunctionsTable.styles';
import LongContentPopover from '@hud/web-components/LongContentPopover/LongContentPopover';
import { CellIcon } from '../styles';
import { EndpointSettingsActionCell } from './EndpointSettingsActionCell';
import { EndpointsSettings } from '@hud/portal-dtos/issues.dtos';

const TABLE_PAGE_SIZE = 5;

export enum EndpointSettingsTableMode {
  Muted = 'muted',
  Unmonitored = 'unmonitored',
}

export type EndpointSettingsTableProps = {
  titleComponent: React.FC<{
    total: number;
  }>;
  endpoints: EndpointsSettings[];
  loading: boolean;
  tableMode: EndpointSettingsTableMode;
};

type FilterType = 'services';

interface Filter {
  type: FilterType;
  // Additional properties that each filter might need for its specific configuration
  props?: unknown; // Use more specific types based on actual requirements
}

const columnHelper = createColumnHelper<EndpointsSettings>();

export default function EndpointSettingsTable({
  titleComponent,
  endpoints,
  loading,
  tableMode,
}: EndpointSettingsTableProps) {
  const globalUrlParams = useGlobalQueryString(); // from the table's search box

  // React-Table Client-Side state
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'serviceName', desc: true },
  ]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: TABLE_PAGE_SIZE,
  });
  const [searchValue, setSearchValue] = useState('');
  const [filteredServices, setFilteredServices] = useState<number[]>([]);

  const [activeFilters, setActiveFilters] = useState<Filter[]>(
    filteredServices.length > 0
      ? [{ type: 'services', props: { ids: filteredServices } }]
      : [],
  );
  const hasFilters = activeFilters.length > 0;
  const [serviceSearchValue, setServiceSearchValue] = useInputState(''); // from the services' filter search box

  const filteredEndpoints = useMemo(() => {
    // client side filtering of issues based on the selected services (search box filterind is done by the table itself using globalSearchFn)
    if (!filteredServices || !filteredServices.length) return endpoints;
    return endpoints.filter((endpoint) =>
      filteredServices.includes(endpoint.serviceId),
    );
  }, [endpoints, filteredServices]);

  const services = useMemo(() => {
    const services: ServicesAutocompleteResponse = endpoints.map((i) => ({
      id: i.serviceId,
      name: i.serviceName,
    }));
    return _.uniqBy(services, (s) => `${s.id}-${s.name}`);
  }, [endpoints]);

  const servicesSelector = useMemo(() => {
    return services.map(({ id, name }) => ({
      value: id.toString(),
      label: name,
    }));
  }, [services]);

  const handleFilterChange = (
    filterType: FilterType,
    isActive: boolean,
    filterProps = {},
  ) => {
    if (isActive) {
      if (activeFilters.some((f) => f.type === filterType)) return;
      setActiveFilters((prev) => [
        ...prev.filter((f) => f.type !== filterType),
        { type: filterType, ...filterProps },
      ]);
    } else {
      setActiveFilters((prev) =>
        prev.filter((filter) => filter.type !== filterType),
      );
    }
  };

  const toggleServiceFilter = (isActive: boolean) => {
    handleFilterChange('services', isActive, { ids: filteredServices });
  };

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('serviceName', {
        header: 'Service',
        size: 300,
        minSize: 200,
        cell: (cell) => (
          <LongContentPopover>
            <CellIcon icon="entities.servicesBlank" size={'20px'} />
            <LinkCell
              to={`/explore/services/${cell.row.original.serviceId}?${globalUrlParams}`}
            >
              {cell.getValue()}
            </LinkCell>
          </LongContentPopover>
        ),
        meta: {
          disableAutoFit: true,
        },
      }),
      columnHelper.accessor('endpointPath', {
        header: 'Endpoint',
        size: 300,
        minSize: 250,
        maxSize: 350,
        cell: (cell) => {
          return (
            <LongContentPopover>
              <CellIcon icon={'webviewMenu.endpoint'} size={'20px'} />
              <LinkCell
                to={`/explore/endpoints/${cell.row.original.endpointId}-${cell.row.original.serviceId}?${globalUrlParams}`}
              >
                {cell.getValue()}
              </LinkCell>
            </LongContentPopover>
          );
        },
        meta: {
          disableAutoFit: true,
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        minSize: 200,
        maxSize: 1000,
        cell: (cell) => {
          return (
            <EndpointSettingsActionCell
              settings={cell.row.original}
              tableMode={tableMode}
            />
          );
        },
        meta: {
          ignoreSize: true,
          disableAutoFit: true,
        },
      }),
    ];
  }, [globalUrlParams]);

  const table = useReactTable({
    data: filteredEndpoints,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),

    onPaginationChange: setPagination,
    onSortingChange: setSorting,

    columnResizeMode: 'onChange',
    state: {
      pagination,
      sorting,
      globalFilter: searchValue,
    },

    enableSortingRemoval: false,
  });

  const TitleComponent = titleComponent;

  const isEmptyState = endpoints.length === 0 && !loading;
  const emptyStateTitle =
    tableMode === EndpointSettingsTableMode.Muted
      ? 'No muted endpoints'
      : 'No unmonitored endpoints';
  const emptyStateIcon =
    tableMode === EndpointSettingsTableMode.Muted
      ? 'other.bellDisabled'
      : 'other.ekgDisabled';

  return (
    <TableContainer.Container
      title={
        loading ? (
          <Skeleton width={140} />
        ) : (
          <TitleComponent total={endpoints.length} />
        )
      }
    >
      <TableContainer.Header>
        <TableContainer.Filters>
          <Search
            inline
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          {activeFilters.map((filter) => {
            switch (filter.type) {
              case 'services':
                return (
                  <MultiSelectFilter
                    label="Services"
                    key={filter.type}
                    options={servicesSelector}
                    onClear={() => {
                      setFilteredServices([]);
                      toggleServiceFilter(false);
                    }}
                    onChange={(value: string[]) => {
                      setFilteredServices(value.map((x) => Number.parseInt(x)));
                    }}
                    onSearch={setServiceSearchValue}
                    searchTerm={serviceSearchValue}
                    onClose={() => setServiceSearchValue('')}
                    multiSelectLabel={
                      filteredServices && filteredServices.length === 1
                        ? services.find(
                            ({ id }) =>
                              id.toString() === filteredServices[0].toString(),
                          )?.name || 'Select...'
                        : filteredServices && filteredServices.length > 1
                          ? `${filteredServices.length} filtered`
                          : 'Select…'
                    }
                    selectedOptions={
                      filteredServices?.map((x) => x.toString()) ?? []
                    }
                  />
                );
              default:
                return null;
            }
          })}
          <Menu.Menu>
            <Menu.Target>
              <div>
                <FiltersMenuButton>
                  <FiltersIcon
                    icon={hasFilters ? 'menu.plus' : 'other.filters'}
                    size={hasFilters ? '14px' : '20px'}
                  />
                  {!hasFilters ? 'Filters' : ''}
                </FiltersMenuButton>
              </div>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => toggleServiceFilter(true)}>
                Services
              </Menu.Item>
            </Menu.Dropdown>
          </Menu.Menu>
          {hasFilters ? (
            <ClearAllButton
              onClick={() => {
                toggleServiceFilter(false);
                setFilteredServices([]);
              }}
            >
              Clear All
            </ClearAllButton>
          ) : null}
        </TableContainer.Filters>
      </TableContainer.Header>

      <HorizontalContainer>
        <DefaultTable
          table={table}
          label="Issues Table"
          loading={loading}
          loadingRows={5}
          options={{
            emptyStateProps: {
              title: emptyStateTitle,
              icon: emptyStateIcon,
            },
          }}
        />
      </HorizontalContainer>

      {!isEmptyState && (
        <TableContainer.Footer>
          <Pagination
            pages={loading ? 0 : table.getPageCount()}
            currentPage={
              loading || table.getPageCount() === 0
                ? 0
                : table.getState().pagination.pageIndex + 1
            }
            onPageChange={(page) => table.setPageIndex(page - 1)}
            loading={loading}
          />
        </TableContainer.Footer>
      )}
    </TableContainer.Container>
  );
}
